import { Immutable } from "immer";
import React, { useActionState } from "react";
import { signupForEmailOffers } from "tsi-common-react/src/api/user";
import { config } from "tsi-common-react/src/config";
import { CaptchaLibrary } from "tsi-common-react/src/forms/Captcha/index";
import {
    FLOATING_LABEL_CLASS_NAME,
    FLOATING_LABEL_USING_PLACEHOLDER_SHOWN_CLASS_NAME,
    PSEUDO_PLACEHOLDER_CLASS_NAME,
} from "tsi-common-react/src/forms/classNames";
import { HttpError } from "tsi-common-react/src/utils/ajax";
import { updateGTMUserHash } from "tsi-common-react/src/utils/analytics";
import { t } from "ttag";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

type IFormState = Immutable<{
    errors: string[];
    isCompleted: boolean;
}>;

const initialState: IFormState = {
    errors: [],
    isCompleted: false,
};

const submitEmailSignup = async (
    _prevState: IFormState,
    formData: FormData,
): Promise<IFormState> => {
    const rawEmail = formData.get("email");
    const email = typeof rawEmail === "string" ? rawEmail : "";
    try {
        await signupForEmailOffers(email);
        updateGTMUserHash(email);
        return { errors: [], isCompleted: true };
    } catch (err) {
        const emailErrors =
            err instanceof HttpError
                ? (err.response.body as { email?: string[] }).email
                : undefined;
        return {
            errors: emailErrors ?? [t`An unexpected error occurred.`],
            isCompleted: false,
        };
    }
};

export const EmailSignupFooter = (): React.ReactNode => {
    const [state, formAction, isPending] = useActionState(
        submitEmailSignup,
        initialState,
    );

    if (state.isCompleted) {
        return (
            <div>
                <div className="email-sign-up l-capped-width">
                    {t`Thank you for subscribing!`}
                </div>
            </div>
        );
    }

    return (
        <div>
            {config.get("ENABLE_EMAIL_SIGNUP_CAPTCHA") && <CaptchaLibrary />}
            <div className="email-sign-up l-capped-width">
                <form className="email-sign-up__form" action={formAction}>
                    <div
                        className={concatClassNames([
                            FLOATING_LABEL_CLASS_NAME,
                            FLOATING_LABEL_USING_PLACEHOLDER_SHOWN_CLASS_NAME,
                            "email-sign-up__email",
                            "email-sign-up__field",
                        ])}
                    >
                        <input
                            autoComplete="email"
                            className="email-sign-up__input"
                            placeholder=" "
                            type="email"
                            name="email"
                            required={true}
                            id="sticky-email-input"
                        />
                        <span
                            className={PSEUDO_PLACEHOLDER_CLASS_NAME}
                            aria-hidden={true}
                        >
                            {t`Enter email to get offers`}
                        </span>
                        <label htmlFor="sticky-email-input">
                            {t`Enter email to get offers`}
                        </label>
                    </div>
                    <div className="email-sign-up__email email-sign-up__submit al-email-signup__submit al-footer-email-signup__submit">
                        <input
                            className="button email-sign-up__button"
                            type="submit"
                            disabled={isPending}
                            value={t`Submit`}
                        />
                        <div className="email-sign-up__diagonal"></div>
                    </div>
                </form>
                <div role="alert">
                    {state.errors.map((msg, i) => (
                        <p key={i} className="email-sign-up__error">
                            {msg}
                        </p>
                    ))}
                </div>
            </div>
        </div>
    );
};
